import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  FormControl,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Box,
  Button,
  Paper,
  TableContainer,
  IconButton,
  Tooltip,
  FormGroup,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  AlertColor,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { FetchedData } from "./ContractorFormPage";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  jaJP,
} from "@mui/x-data-grid";
import {
  StyledDataGrid,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import { Flex } from "@aws-amplify/ui-react";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import React from "react";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { useNavigate, useParams } from "react-router-dom";

type ICCardKeyTableProps = {
  buildingData: FetchedData["building"];
  keysData: FetchedData["icCardKeys"];
  fetchedAt: number;
  isEnabled: boolean | undefined;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
};

export type ICCardKey = {
  autoIcCardKeyId: number | null;
  keyIndexInContract: number;
  autoSdcId: number;
  doorPlace: string;
  issuedAt: Date;
  lastUsedAt: Date | null;
  idm: string;
  isEnabled: boolean;
  isDelete: boolean;
  memo: string;
};

type Sdc = {
  autoSdcId: number;
  doorPlace: string;
};

const isHexadecimal = (str: any) => /^[a-f0-9]+$/.test(str);

const ICCardKeyTable = ({
  buildingData,
  keysData,
  fetchedAt,
  isEnabled,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}: ICCardKeyTableProps) => {
  const { autoBuildingId, autoContractId } = useParams(); //URLパラメータから取得

  const [icCardKeys, setIcCardKeys] = useState<ICCardKey[]>(() => {
    return keysData.map((key) => ({
      autoIcCardKeyId: key.autoIcCardKeyId,
      keyIndexInContract: key.keyIndexInContract,
      autoSdcId: key.sdc.autoSdcId,
      doorPlace: key.sdc.doorPlace,
      issuedAt: new Date(key.createdAt),
      lastUsedAt: key.lastUsedAt ? new Date(key.lastUsedAt) : null,
      idm: key.idm,
      isEnabled: key.isEnabled,
      isDelete: false,
      memo: key.memo,
    }));
  });

  const [selectDoor, setSelectDoor] = useState<Sdc>({
    autoSdcId: -1,
    doorPlace: "",
  });
  const [memo, setMemo] = useState<string>("");
  const [idm, setIdm] = useState<string>("");

  // バリデーションエラーの状態
  const [validationErrors, setValidationErrors] = useState({
    door: "",
    idm: "",
    memo: "",
  });

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  // バリデーションロジック
  useEffect(() => {
    const errors = {
      door: "",
      idm: "",
      memo:
        memo.length > CHARACTER_LIMIT
          ? "メモは50文字以内で入力してください"
          : "",
    };
    setValidationErrors(errors);
  }, [idm, memo, selectDoor]);

  const saveCard = async () => {
    await addCardKey();
  };

  // キーの情報を取得し直す
  const fetchNewKeys = async () => {
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();
    try {
      fetchedAt = new Date().getTime();
      // call showContract API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          "/buildings/" +
          autoBuildingId +
          "/contracts/" +
          autoContractId,
        {
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching data");
      }

      const jsonData = await response.json();
      const keysData = await jsonData["icCardKeys"];

      setIcCardKeys(() => {
        return keysData.map((key: any) => ({
          autoIcCardKeyId: key.autoIcCardKeyId,
          keyIndexInContract: key.keyIndexInContract,
          autoSdcId: key.sdc.autoSdcId,
          doorPlace: key.sdc.doorPlace,
          issuedAt: new Date(key.createdAt),
          lastUsedAt: key.lastUsedAt,
          idm: key.idm,
          isEnabled: key.isEnabled,
          isDelete: false,
          memo: key.memo,
        }));
      });

      console.log(
        "fetchedIcCardKeys:" + JSON.stringify(jsonData["icCardKeys"])
      );
    } catch (error) {
      console.error("Error updating ic card key:", error);
    }

    // 入力を空に
    setMemo("");
    setIdm("");
    setSelectDoor({ autoSdcId: -1, doorPlace: "" });
  };

  const addCardKey = async () => {
    var valid: boolean = true;
    // 対象ドアに同一のidmがすでに登録されている場合
    if (
      icCardKeys.find(
        (key) => key.autoSdcId == selectDoor.autoSdcId && key.idm == idm
      ) != undefined
    ) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        idm: "このカードはすでに対象ドアに登録されています",
      }));
      valid = false;
    }
    // IDが16文字でない場合
    else if (!isHexadecimal(idm) || idm.length != 16) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        idm: "カードIDが正しくありません",
      }));
      valid = false;
    }
    if (selectDoor.autoSdcId == -1) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        door: "ドアを選択してください",
      }));
      valid = false;
    }
    if (!valid) return;

    const date = new Date();
    const idx =
      icCardKeys.length == 0
        ? 0
        : icCardKeys[icCardKeys.length - 1]["keyIndexInContract"];

    const newIcCardKey = {
      autoIcCardKeyId: null,
      keyIndexInContract: idx + 1,
      autoSdcId: selectDoor.autoSdcId,
      doorPlace: selectDoor.doorPlace,
      issuedAt: date,
      lastUsedAt: null,
      idm: idm,
      isEnabled: true,
      isDelete: false,
      memo: memo,
    };

    setIcCardKeys([...icCardKeys, newIcCardKey]);

    console.log("add card key: " + JSON.stringify(newIcCardKey));

    // 入力を空に
    setMemo("");
    setIdm("");
    setSelectDoor({
      autoSdcId: -1,
      doorPlace: "",
    });
  };

  const handleUpdateAll = async () => {
    await updateIcCardKey();
  };

  const updateIcCardKey = async () => {
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();
    try {
      // call updateIcCardKeys API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/contracts/${autoContractId}/ic-card-keys`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            lastTsFetchedAt: fetchedAt,
            icCardKeys: icCardKeys.filter((key) => !key.isDelete),
          }),
        }
      );

      if (!response.ok) {
        console.log("response.status:" + response.status);
        // HTTP status code indicates an error - throw an Error with message from the response
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const responseData = await response.json();
      console.log("レスポンスデータ：" + JSON.stringify(responseData));

      setSnackbarMessage("更新が完了しました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);
      await fetchNewKeys();
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Error updating master key:", error);
        setSnackbarMessage("更新に失敗しました。エラー：" + error.message);
        setAlertSeverity("error");
        setSnackbarOpen(true);
      } else {
        console.error("Unknown error caught:", error);
        setSnackbarMessage("更新に失敗しました。不明なエラーが発生しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleDoorChange = (event: any) => {
    const selectValue = event.target.value;
    const sdc = buildingData.sdcs.find((sdc) => sdc.doorPlace === selectValue);
    if (sdc) {
      setSelectDoor(sdc);
    }
  };

  // keyIndexInContractがidと等しい行のkeyの値をvalueに更新する
  const handleIcCardKeyChange = (id: number, key: string, value: any) => {
    setIcCardKeys((prevCardKeys) =>
      prevCardKeys.map((cardKey) =>
        cardKey.keyIndexInContract === id
          ? { ...cardKey, [key]: value }
          : cardKey
      )
    );
    console.log("カードキー" + JSON.stringify(icCardKeys));
  };

  const dateOf = (params: any) => {
    const formattedDate = params.value
      ? new Date(params.value).toLocaleString()
      : "";
    return (
      <Tooltip title={formattedDate}>
        <span>{formattedDate}</span>
      </Tooltip>
    );
  };

  console.log("keysData: " + keysData);

  const columns: GridColDef[] = [
    {
      field: "keyIndexInContract",
      headerName: "登録No",
      flex: 0.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "doorPlace",
      headerName: "対象ドア",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "issuedAt",
      headerName: "登録日時",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={dateOf(params)}>
          <span>{dateOf(params)}</span>
        </Tooltip>
      ),
    },
    {
      field: "lastUsedAt",
      headerName: "最終使用日時",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={dateOf(params)}>
          <span>{dateOf(params)}</span>
        </Tooltip>
      ),
    },
    {
      field: "idm",
      headerName: "カードID(IDm)",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "isEnabled",
      headerName: "有効・無効",
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => (
        <FormControl component="fieldset">
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={params.value}
                  onChange={(e) => {
                    handleIcCardKeyChange(
                      params.row.keyIndexInContract,
                      "isEnabled",
                      e.target.checked
                    );
                  }}
                  disabled={params.row.isDelete || !isEnabled}
                />
              }
              label={params.value ? "有効" : "無効"}
            />
          </FormGroup>
        </FormControl>
      ),
    },
    {
      field: "isDelete",
      headerName: "削除",
      flex: 0.7,
      renderCell: (params) =>
        params.value ? (
          // params.valueがtrue、つまり削除予定なら
          isEnabled ? (
            <Tooltip title="削除予定">
              <Button
                fullWidth
                sx={{
                  display: "inline",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  handleIcCardKeyChange(
                    params.row.keyIndexInContract,
                    "isDelete",
                    false
                  );
                }}
                variant={params.value ? "outlined" : "text"}
              >
                削除予定
              </Button>
            </Tooltip>
          ) : (
            <Button
              disabled
              fullWidth
              sx={{
                display: "inline",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
              variant={params.value ? "outlined" : "text"}
            >
              削除予定
            </Button>
          )
        ) : isEnabled ? (
          <Tooltip title="削除">
            <Button
              fullWidth
              onClick={() => {
                handleIcCardKeyChange(
                  params.row.keyIndexInContract,
                  "isDelete",
                  true
                );
              }}
            >
              削除
            </Button>
          </Tooltip>
        ) : (
          <Button disabled fullWidth>
            削除
          </Button>
        ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <TextField
            size="small"
            defaultValue={params.value}
            onBlur={(e) => {
              handleIcCardKeyChange(
                params.row.keyIndexInContract,
                "memo",
                e.target.value
              );
            }}
            onKeyDown={(e) => {
              e.stopPropagation(); // キーイベントの伝播を停止
            }}
            disabled={params.row.isDelete || !isEnabled}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Grid container rowSpacing={3}>
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">発行済みICカードキー一覧</Typography>
      </Grid>
      <Grid item>
        <TableContainer>
          <Table size="small" aria-label="simple table">
            <TableHead>
              <StyledTableRow>
                <TableCell>対象ドア</TableCell>
                <TableCell>カードID(IDm)</TableCell>
                <TableCell>メモ</TableCell>
                <TableCell style={{ borderBottom: 0 }}> </TableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              <StyledTableRow>
                <StyledTableCell>
                  <FormControl
                    disabled={!isEnabled}
                    sx={{ minWidth: 200 }}
                    size="small"
                  >
                    <Select
                      value={selectDoor.doorPlace}
                      onChange={(e) => handleDoorChange(e)}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                      error={validationErrors.door !== ""}
                    >
                      {buildingData.sdcs.map((door) => (
                        <MenuItem value={door.doorPlace} key={door.autoSdcId}>
                          {door.doorPlace}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.door !== "" ? (
                      <FormHelperText error>
                        {validationErrors.door}
                      </FormHelperText>
                    ) : (
                      <div></div>
                    )}
                  </FormControl>
                </StyledTableCell>
                <StyledTableCell>
                  <TextField
                    disabled={!isEnabled}
                    size="small"
                    sx={{ minWidth: 300 }}
                    value={idm}
                    onChange={(e) => setIdm(e.target.value.toLowerCase())}
                    helperText={validationErrors.idm}
                    error={validationErrors.idm !== ""}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <TextField
                    disabled={!isEnabled}
                    size="small"
                    sx={{ minWidth: 150 }}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    helperText={validationErrors.memo}
                    error={validationErrors.memo !== ""}
                  />
                </StyledTableCell>
                <StyledTableCell style={{ padding: 13 }}>
                  <Button
                    disabled={!isEnabled}
                    variant="outlined"
                    color="primary"
                    onClick={saveCard}
                    size="medium"
                  >
                    ICカード追加
                  </Button>
                  <FormHelperText>
                    ※ICカードキー情報保存ボタンを押すまで保存されません
                  </FormHelperText>
                </StyledTableCell>
              </StyledTableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} justifySelf="center">
        <StyledDataGrid
          sx={isEnabled ? { border: 0 } : { border: 0, background: "#e0e0e0" }}
          rows={icCardKeys}
          columns={columns}
          autoHeight
          localeText={
            icCardKeys.length === 0 ? customDataGridLocaleTextNoData : {}
          }
          getRowId={(row) => row.keyIndexInContract}
          getRowClassName={(rows) => {
            if (rows.row.isDelete) {
              return "row-delete";
            }
            return "";
          }}
        />
      </Grid>

      <Grid item xs={12} marginTop={2}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateAll}
            style={{ marginRight: "12px" }}
            disabled={!isEnabled}
          >
            ICカードキー情報保存
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ICCardKeyTable;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-child th": {
    borderBottom: "2px solid #000",
  },
  "&:last-child td": {
    borderBottom: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: 10,
  verticalAlign: "top",
}));
